<script lang="ts">
  // Signup control — the access-request pipeline, laid out in flow order:
  //   POLICY (what happens when someone new signs in)
  //     → ① request waiting on the admin
  //     → ② key issued, waiting on them
  //     → ③ active (managed on the Users screen)
  // Approving mints a one-time 16-hex activation key and emails it to them with a
  // copy to the administrator. Inviting a specific person by address is on Users.
  import { getSignup, setSignup, approveMember, declineMember } from '../lib/signupConfig';
  import { listMembers, type Member } from '../lib/members';

  let inviteOnly = $state(true);
  let updatedAt = $state(0);
  let emailConfigured = $state(false);
  let loading = $state(true);
  let error = $state('');
  let saving = $state(false);
  let saved = $state(false);

  let rows = $state<Member[]>([]);
  let busy = $state('');                                   // email mid-approval
  let copied = $state('');                                 // email whose key was just copied
  // Keys minted this session. They exist ONLY here — the backend keeps just a
  // SHA-256 — so they vanish on reload and must be re-issued to be seen again.
  let issued = $state<Record<string, { code: string; emailed: boolean }>>({});

  const pending = $derived(rows.filter((m) => m.status === 'pending'));
  const awaiting = $derived(rows.filter((m) => m.status === 'invited'));
  const activeCount = $derived(rows.filter((m) => m.status === 'active').length);

  async function load() {
    loading = true; error = '';
    try {
      const c = await getSignup();
      inviteOnly = c.inviteOnly; updatedAt = c.updatedAt; emailConfigured = c.emailConfigured;
    } catch (e) { error = (e as Error)?.message || String(e); }
    try { rows = await listMembers(); } catch { /* registry unreadable — queue stays empty */ }
    loading = false;
  }
  $effect(() => { load(); });

  async function save() {
    saving = true; error = ''; saved = false;
    try { await setSignup({ inviteOnly }); saved = true; setTimeout(() => (saved = false), 2500); }
    catch (e) { error = (e as Error)?.message || String(e); }
    finally { saving = false; }
  }

  async function approve(m: Member) {
    busy = m.email;
    try {
      const r = await approveMember(m.email);
      issued = { ...issued, [m.email]: { code: r.code, emailed: r.emailed } };
      rows = rows.map((x) => (x.email === m.email ? { ...x, status: 'invited' as const } : x));
    } catch (e) { alert(`Could not approve ${m.email}: ${(e as Error)?.message ?? e}`); }
    finally { busy = ''; }
  }

  async function decline(m: Member) {
    if (!confirm(`Decline ${m.email}? Their request is removed and they stay locked out.`)) return;
    busy = m.email;
    try { await declineMember(m.email); rows = rows.filter((x) => x.email !== m.email); }
    catch (e) { alert(`Could not decline: ${(e as Error)?.message ?? e}`); }
    finally { busy = ''; }
  }

  const fmtCode = (c: string) => c.replace(/(.{4})(?=.)/g, '$1 ');

  async function copyKey(email: string) {
    const k = issued[email]; if (!k) return;
    try { await navigator.clipboard.writeText(k.code); copied = email; setTimeout(() => (copied = ''), 2000); } catch { /* select it manually */ }
  }

  const APP_URL = 'https://health.dreamgiga.ai';
  function mailtoLink(m: Member): string {
    const k = issued[m.email]; if (!k) return '#';
    const subject = 'Your MPWRD activation key';
    const body = `Your request to join MPWRD has been approved.\n\n`
      + `Sign in at ${APP_URL} with this Google account (${m.email}), then enter your one-time activation key when prompted:\n\n`
      + `    ${fmtCode(k.code)}\n\n`
      + `This key works once and is tied to your email address.`;
    return `mailto:${encodeURIComponent(m.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  // requestedAt / invitedAt are ISO strings from the backend; older rows may hold
  // a Firestore timestamp instead.
  function toMs(v: unknown): number {
    if (!v) return 0;
    if (typeof v === 'string') return Date.parse(v) || 0;
    const o = v as { toMillis?: () => number; seconds?: number };
    if (typeof o.toMillis === 'function') return o.toMillis();
    if (typeof o.seconds === 'number') return o.seconds * 1000;
    return 0;
  }
  function fmtWhen(v: unknown): string {
    const ms = toMs(v); if (!ms) return '';
    const s = Math.floor((Date.now() - ms) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
    return new Date(ms).toLocaleDateString();
  }
  const fmtStamp = (ms: number) => (ms ? new Date(ms).toLocaleString() : 'never');
</script>

<div class="body">
  <div class="head">
    <div>
      <h1>Signup control</h1>
      <p class="muted">What happens when someone new signs in — and everyone currently part-way through.</p>
    </div>
    <button class="refresh" onclick={load} disabled={loading}>{loading ? 'Loading…' : '⟳ Refresh'}</button>
  </div>

  {#if error}<div class="banner err"><strong>Error.</strong> {error}</div>{/if}

  {#if !loading && !emailConfigured}
    <div class="banner warn">
      <strong>Automatic email is unavailable.</strong> The backend can't reach the Twilio sending keys, so approving still mints the key — deliver it yourself with the copy / email-draft buttons.
    </div>
  {/if}

  <!-- ─────────── THE GATE: policy that governs everything below ─────────── -->
  <section class="stage policy">
    <div class="eyebrow"><span class="tag">Policy</span><span class="rule"></span></div>
    <div class="panel">
      <h2>When someone new signs in with Google…</h2>
      <div class="choices" role="radiogroup" aria-label="Signup policy">
        <button
          class="choice" class:sel={inviteOnly} type="button" role="radio" aria-checked={inviteOnly}
          onclick={() => (inviteOnly = true)}
        >
          <span class="mark" aria-hidden="true"></span>
          <span class="ctext">
            <span class="ctitle">Hold for approval <span class="rec">recommended</span></span>
            <span class="cdesc">They land in the queue below and can read or write nothing until you approve them and they enter a key.</span>
          </span>
        </button>
        <button
          class="choice danger" class:sel={!inviteOnly} type="button" role="radio" aria-checked={!inviteOnly}
          onclick={() => (inviteOnly = false)}
        >
          <span class="mark" aria-hidden="true"></span>
          <span class="ctext">
            <span class="ctitle">Let them straight in</span>
            <span class="cdesc">Anyone with a Google account is activated immediately — no approval, no key, no queue.</span>
          </span>
        </button>
      </div>
      <div class="savebar">
        <span class="stamp">Last changed {fmtStamp(updatedAt)}</span>
        <button class="primary" onclick={save} disabled={saving || loading}>{saving ? 'Saving…' : saved ? '✓ Saved' : 'Save policy'}</button>
      </div>
    </div>
  </section>

  {#if inviteOnly}
    <div class="flow" aria-hidden="true"><span class="line"></span><span class="chev">▼</span></div>
  {:else}
    <div class="bypass">Open signup is on — new sign-ins skip the steps below entirely.</div>
  {/if}

  <!-- ─────────── STEP 1: waiting on you ─────────── -->
  <section class="stage" class:dim={!inviteOnly}>
    <div class="eyebrow"><span class="num">1</span><span class="steplabel">Waiting on you</span><span class="rule"></span></div>
    <div class="panel">
      <div class="panelhead">
        <h2>Requests awaiting approval {#if pending.length}<span class="badge">{pending.length}</span>{/if}</h2>
        <p class="muted">Approving mints a one-time key and emails it to them, with a copy to you.</p>
      </div>

      {#if loading}
        <p class="faint pad">Loading…</p>
      {:else if pending.length === 0}
        <p class="faint pad">No one is waiting. New requests appear here — and land in your inbox — the moment someone signs in.</p>
      {:else}
        <ul class="list">
          {#each pending as m (m.email)}
            <li class="row">
              <div class="who">
                <span class="name">{m.displayName || m.email}</span>
                <span class="mono sub">{m.email}</span>
                {#if fmtWhen(m.requestedAt)}<span class="sub">requested {fmtWhen(m.requestedAt)}</span>{/if}
              </div>
              <div class="acts">
                <button class="primary" onclick={() => approve(m)} disabled={busy === m.email}>
                  {busy === m.email ? 'Approving…' : 'Approve & send key'}
                </button>
                <button class="ghost" onclick={() => decline(m)} disabled={busy === m.email}>Decline</button>
              </div>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  </section>

  <div class="flow" aria-hidden="true"><span class="line"></span><span class="chev">▼</span></div>

  <!-- ─────────── STEP 2: waiting on them ─────────── -->
  <section class="stage" class:dim={!inviteOnly}>
    <div class="eyebrow"><span class="num">2</span><span class="steplabel">Waiting on them</span><span class="rule"></span></div>
    <div class="panel">
      <div class="panelhead">
        <h2>Key issued — awaiting activation {#if awaiting.length}<span class="badge quiet">{awaiting.length}</span>{/if}</h2>
        <p class="muted">They enter the key in the app to finish. Re-issuing invalidates the previous key.</p>
      </div>

      {#if loading}
        <p class="faint pad">Loading…</p>
      {:else if awaiting.length === 0}
        <p class="faint pad">Nobody is mid-activation.</p>
      {:else}
        <ul class="list">
          {#each awaiting as m (m.email)}
            <li class="row">
              <div class="who">
                <span class="name">{m.displayName || m.email}</span>
                <span class="mono sub">{m.email}</span>
                {#if fmtWhen(m.approvedAt || m.invitedAt)}<span class="sub">key issued {fmtWhen(m.approvedAt || m.invitedAt)}</span>{/if}
              </div>
              <div class="acts">
                <button class="secondary" onclick={() => approve(m)} disabled={busy === m.email} title="Mint a replacement key — the previous one stops working">
                  {busy === m.email ? 'Issuing…' : issued[m.email] ? 'Re-issue key' : 'Issue new key'}
                </button>
              </div>
            </li>
            {#if issued[m.email]}
              <li class="keycard">
                <div class="keytop">
                  <span class="keylabel">Activation key for {m.email}</span>
                  <span class="delivery {issued[m.email].emailed ? 'sent' : 'unsent'}">
                    {issued[m.email].emailed ? '✓ Emailed to them (copy sent to you)' : '⚠ Not emailed — deliver manually'}
                  </span>
                </div>
                <div class="keyrow">
                  <code class="bigkey">{fmtCode(issued[m.email].code)}</code>
                  <button class="secondary" onclick={() => copyKey(m.email)}>{copied === m.email ? 'Copied ✓' : 'Copy key'}</button>
                  <a class="secondary linkbtn" href={mailtoLink(m)}>Open email draft</a>
                </div>
                <p class="hint">Shown once — only its hash is stored. Reload and it's gone; issue a new key if it's lost.</p>
              </li>
            {/if}
          {/each}
        </ul>
      {/if}
    </div>
  </section>

  <div class="flow" aria-hidden="true"><span class="line"></span><span class="chev">▼</span></div>

  <!-- ─────────── STEP 3: done ─────────── -->
  <section class="stage" class:dim={!inviteOnly}>
    <div class="eyebrow"><span class="num done">✓</span><span class="steplabel">Done</span><span class="rule"></span></div>
    <div class="panel terminal">
      <div>
        <h2>Active — full access</h2>
        <p class="muted">{activeCount} member{activeCount === 1 ? '' : 's'} activated. Roles, blocking and removal live on the Users screen.</p>
      </div>
    </div>
  </section>

  <p class="footnote">
    Keys are 16 hexadecimal characters, single-use, and stored only as a SHA-256 hash — never in readable form.
    To invite a specific person by address instead of waiting for them to ask, use <b>Users</b> → Add / invite.
  </p>
</div>

<style>
  .body { width: 100%; max-width: 860px; padding: var(--space-6) var(--space-8); display: flex; flex-direction: column; gap: 0; }
  .head { display: flex; align-items: flex-end; justify-content: space-between; gap: var(--space-4); flex-wrap: wrap; margin-bottom: var(--space-5); }
  h1 { font-weight: var(--fw-heading); letter-spacing: -0.01em; }
  h2 { font-size: var(--fs-body); font-weight: var(--fw-heading); display: inline-flex; align-items: center; gap: var(--space-2); }
  .muted { color: var(--text-3); font-size: var(--fs-sm); margin-top: 3px; max-width: 64ch; line-height: 1.5; }
  .faint { color: var(--text-3); font-size: var(--fs-sm); }
  .pad { padding: var(--space-4); }

  .refresh { padding: var(--space-2) var(--space-4); border-radius: var(--radius-pill); background: var(--surface-2); color: var(--text-2); border: 1px solid var(--line-strong); font-weight: 700; font-size: var(--fs-xs); }
  .refresh:hover:not(:disabled) { color: var(--accent); border-color: color-mix(in srgb, var(--accent) 40%, transparent); }
  .refresh:disabled { opacity: 0.6; cursor: default; }

  .banner { padding: var(--space-3) var(--space-4); border-radius: var(--radius-md); font-size: var(--fs-xs); line-height: 1.5; margin-bottom: var(--space-4); }
  .banner.warn { color: var(--warn); background: var(--warn-soft); border: 1px solid color-mix(in srgb, var(--warn) 34%, transparent); }
  .banner.err { color: var(--text-2); background: var(--surface); border: 1px solid var(--line-strong); }

  /* ── stage = one labelled step in the flow ── */
  .stage { display: flex; flex-direction: column; gap: var(--space-2); transition: opacity 0.18s ease; }
  .stage.dim { opacity: 0.55; }
  .eyebrow { display: flex; align-items: center; gap: var(--space-2); }
  .tag { font-size: var(--fs-2xs); font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: var(--accent); }
  .num { width: 20px; height: 20px; flex: none; display: grid; place-items: center; border-radius: 50%; background: var(--accent); color: var(--on-accent); font-size: var(--fs-2xs); font-weight: 800; }
  .num.done { background: var(--ok); }
  .steplabel { font-size: var(--fs-2xs); font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: var(--text-3); }
  .rule { flex: 1; height: 1px; background: var(--line); }

  .panel { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); overflow: hidden; }
  .panelhead { padding: var(--space-4); }
  .policy .panel { border-color: color-mix(in srgb, var(--accent) 34%, transparent); background: color-mix(in srgb, var(--accent) 5%, var(--surface)); padding: var(--space-4); }
  .policy h2 { display: block; margin-bottom: var(--space-3); }
  .terminal { padding: var(--space-4); }

  /* ── connector between stages ── */
  .flow { display: flex; flex-direction: column; align-items: center; gap: 1px; padding: var(--space-2) 0; }
  .flow .line { width: 1px; height: 14px; background: var(--line-strong); }
  .flow .chev { font-size: 9px; line-height: 1; color: var(--line-strong); }
  .bypass { margin: var(--space-3) 0; padding: var(--space-2) var(--space-4); border-left: 2px solid var(--warn); background: var(--warn-soft); color: var(--warn); font-size: var(--fs-xs); font-weight: 700; border-radius: 0 var(--radius-sm) var(--radius-sm) 0; }

  /* ── policy choices ── */
  .choices { display: flex; flex-direction: column; gap: var(--space-2); }
  .choice { display: flex; align-items: flex-start; gap: var(--space-3); text-align: left; padding: var(--space-3); border-radius: var(--radius-md); border: 1px solid var(--line); background: var(--surface-2); cursor: pointer; width: 100%; }
  .choice:hover { border-color: color-mix(in srgb, var(--accent) 40%, transparent); }
  .choice.sel { border-color: var(--accent); background: color-mix(in srgb, var(--accent) 12%, var(--surface-2)); }
  .choice.danger.sel { border-color: var(--warn); background: var(--warn-soft); }
  .mark { width: 16px; height: 16px; flex: none; margin-top: 2px; border-radius: 50%; border: 2px solid var(--line-strong); }
  .choice.sel .mark { border-color: var(--accent); background: radial-gradient(circle, var(--accent) 0 45%, transparent 46%); }
  .choice.danger.sel .mark { border-color: var(--warn); background: radial-gradient(circle, var(--warn) 0 45%, transparent 46%); }
  .ctext { display: flex; flex-direction: column; gap: 2px; }
  .ctitle { font-weight: 700; font-size: var(--fs-sm); display: inline-flex; align-items: center; gap: var(--space-2); }
  .rec { font-size: var(--fs-2xs); font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--accent); }
  .cdesc { font-size: var(--fs-xs); color: var(--text-3); line-height: 1.5; }
  .savebar { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); margin-top: var(--space-3); flex-wrap: wrap; }
  .stamp { font-size: var(--fs-2xs); color: var(--text-3); }

  /* ── queues ── */
  .badge { font-size: var(--fs-2xs); font-weight: 800; padding: 2px 9px; border-radius: var(--radius-pill); background: var(--accent); color: var(--on-accent); }
  .badge.quiet { background: var(--surface-2); color: var(--text-2); border: 1px solid var(--line-strong); }
  .list { list-style: none; margin: 0; padding: 0; border-top: 1px solid var(--line); }
  .row { display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); padding: var(--space-3) var(--space-4); border-bottom: 1px solid var(--line); flex-wrap: wrap; }
  .list li:last-child { border-bottom: none; }
  .who { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .name { font-weight: 600; }
  .mono { font-family: var(--mono); }
  .sub { font-size: var(--fs-xs); color: var(--text-3); }
  .acts { display: inline-flex; gap: var(--space-2); flex-wrap: wrap; }

  .primary { padding: var(--space-2) var(--space-4); border-radius: var(--radius-pill); background: var(--accent-grad, var(--accent)); color: var(--on-accent); font-weight: 700; font-size: var(--fs-xs); }
  .primary:disabled { opacity: 0.6; cursor: default; }
  .secondary { padding: var(--space-2) var(--space-4); border-radius: var(--radius-pill); background: var(--surface-2); color: var(--text-2); border: 1px solid var(--line-strong); font-weight: 700; font-size: var(--fs-xs); }
  .secondary:hover { color: var(--accent); border-color: color-mix(in srgb, var(--accent) 40%, transparent); }
  .linkbtn { text-decoration: none; display: inline-flex; align-items: center; }
  .ghost { padding: var(--space-2) var(--space-4); border-radius: var(--radius-pill); background: transparent; color: var(--text-3); border: 1px solid var(--line-strong); font-weight: 700; font-size: var(--fs-xs); }
  .ghost:hover { color: var(--danger); border-color: var(--danger); }

  .keycard { padding: var(--space-4); border-bottom: 1px solid var(--line); background: var(--accent-soft); display: flex; flex-direction: column; gap: var(--space-2); }
  .keytop { display: flex; justify-content: space-between; gap: var(--space-3); flex-wrap: wrap; }
  .keylabel { font-size: var(--fs-xs); font-weight: 700; color: var(--text-2); }
  .delivery { font-size: var(--fs-2xs); font-weight: 700; }
  .delivery.sent { color: var(--ok); }
  .delivery.unsent { color: var(--warn); }
  .keyrow { display: flex; align-items: center; gap: var(--space-3); flex-wrap: wrap; }
  .bigkey { font-family: var(--mono); font-size: var(--fs-lg, 1.15rem); font-weight: 800; letter-spacing: 0.14em; color: var(--accent); user-select: all; }
  .hint { font-size: var(--fs-2xs); color: var(--text-3); }

  .footnote { color: var(--text-3); font-size: var(--fs-2xs); line-height: 1.6; margin-top: var(--space-5); }
  .footnote b { color: var(--text-2); }
</style>
